import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from "react-redux";
import { EditorState, ContentState, convertToRaw, convertFromHTML, RichUtils, convertFromRaw } from 'draft-js';
import 'draft-js/dist/Draft.css'
import draftToHtml from 'draftjs-to-html';
import { useDebouncedCallback } from 'use-debounce';
import MUIRichTextEditor from "mui-rte";
import punycode from 'punycode';

import { InputLabel, FormHelperText } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';

import makePath from '../../utils/makePath';
import generateId from '../../utils/generateId';
import { setItem } from "../../actions";
import { useDirty, useFormElementCleanUp, useValidation } from '../../hooks';
import { FormElementWrapper } from './FormElementWrapper';
import { ErrorHelpText } from './ErrorHelpText';


let validationCriteria = {
  mustChange: true,
  optional: false,
}

const htmlToContentState = (html) => {
  const contentHTML = convertFromHTML(html)
  const state = ContentState.createFromBlockArray(contentHTML.contentBlocks, contentHTML.entityMap)
  return JSON.stringify(convertToRaw(state))
}

const useStyles = makeStyles((theme) => ({
  root: {
    color: theme.palette.neutral.med,
  },
  rootError: {
    color: theme.palette.error.main,
  },
  charCount: {
    fontWeight: 500,
    color: theme.palette.neutral.dark3
  },
  inputLabel: {
    top: -7,
    position: 'relative',
  },
}))


export const RichTextEditor = React.memo(({callback,defaultValue='',given,max,name,controls = [],updateParent, ...props}) => {
  const [id] = useState(generateId('rte-'));
  const classes = useStyles();
  const dispatch = useDispatch();
  const setDirty = useDirty();

  const path = makePath(props.path,name); 
  useFormElementCleanUp(path);

  const [content, setContentState] = useState(htmlToContentState(defaultValue));
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [charCount, setCharCount] = useState(0);


  const validate = () => {

    if (props.validationCriteria) {
      validationCriteria = props.validationCriteria
    }

    const plainValue = editorState.getCurrentContent().getPlainText('');
    
    // Replicate behavior of setting default value so it can be compared with current state.
    // This is to handle a default state with html in it
    const defaultState = EditorState.createWithContent(convertFromRaw(JSON.parse(htmlToContentState(defaultValue))), '');
    const defaultRaw = convertToRaw(defaultState.getCurrentContent());
    const defaultHtml = draftToHtml(defaultRaw);

    const raw = convertToRaw(editorState.getCurrentContent());
    const html = draftToHtml(raw);

    if (!plainValue && !validationCriteria.optional) {
      setError('Missing! This field is required.');
    } else if ( ( html === defaultHtml || plainValue === defaultValue ) && validationCriteria.mustChange) {
      setError('Missing! You need to replace the default text.');
    } else if (max && plainValue.length > max ) {
      setError('Error! You have exceeded the character limit.');
    } else {
      setError('');
    }
    
  }

  const [error, setError] = useValidation(validate,editorState,path,id);




  const getCharCount = editorState => {

    const decodeUnicode = str => punycode.ucs2.decode(str); // func to handle unicode characters
    const plainText = editorState.getCurrentContent().getPlainText('');
    const regex = /(?:\r\n|\r|\n)/g; // new line, carriage return, line feed
    const cleanString = plainText.replace(regex, '').trim(); // replace above characters w/ nothing
    
    return decodeUnicode(cleanString).length;

  };


  const onRTEChange = (editorState) => {
    // last change type is null until a text change has occurred
    if ( editorState.getLastChangeType() ) {
      setDirty();
    }
    setEditorState(editorState)
  }


  const debounced = useDebouncedCallback(
    (value) => {
      if (updateParent) {
        updateParent({[name]: value});
      } else {
        dispatch(setItem(path,value));
        if (callback) {
          callback(name,editorState.getCurrentContent().getPlainText(''))
        }
      }
    },
    300
  );


  // Update store/parent when editorState updates.
  // Include all vars from debounced in dependency array 
  // because useDebouncedCallback doesn't have dependencies array
  useEffect(() => {
    const raw = convertToRaw(editorState.getCurrentContent());
    const html = draftToHtml(raw);
    debounced.callback(html)
  }, [callback,debounced,dispatch,editorState,name,path,updateParent])


  // Disallow Ctrl/Cmd + B, I, or U to bold, italicize or underline
  // when those commands are not in the list of controls
  const generateKeyCommands = (controls) => {
    const commands = [];

    const doNothing =  (editorState) => {
      return editorState
    }

    if (controls.indexOf('bold') < 0 ) {
      commands.push({
        key: 66, // B
        name: "no-bold",
        callback: doNothing
      })
    }
    if (controls.indexOf('italic') < 0 ) {
      commands.push({
        key: 73, // I
        name: "no-italic",
        callback: doNothing
      })
    }
    if (controls.indexOf('underline') < 0 ) {
      commands.push({
        key: 85, // I
        name: "no-underline",
        callback: doNothing
      })
    }

    return commands;
  }


  // Seed editorState with value provided by parent, if applicable
  useEffect(() => {
    if (given) {
      const contentState = htmlToContentState(given)
      setContentState(contentState)
    }
  }, [given]);


  // If there is a maximum character limit, keep
  // track of the character count
  useEffect(() => {
    if (max) {
      const count = getCharCount(editorState);
      setCharCount(count);
    }
  }, [editorState,max])


  /**
   * 
   * @todo
   * Console warning:
   * Supplying an `onTab` prop to `DraftEditor` has been deprecated. 
   * If your handler needs access to the keyboard event, supply a custom `keyBindingFn` 
   * prop that falls back to the default one (eg. https://is.gd/wHKQ3W).
   */
  const handleTab = e => {
    //prevent cursor from selecting the next interactive element
    e.preventDefault()

    // assign a constant for the new editorState
    const newState = RichUtils.onTab(e, editorState, 2)

    // if a new editor state exists, set editor state to new state
    // and return 'handled', otherwise return 'not-handled
    if (newState) {
      setEditorState(newState)
      return 'handled';
    } else {
      return 'not-handled';
    }
  }


  const draftEditorProps = {
    stripPastedStyles: true,
    editorState
  };


  if (!props.disallowIndent) {
    draftEditorProps.onTab = handleTab
  }


  return (
    <FormElementWrapper className={error ? classes.rootError : classes.root} fullWidth style={props.label ? {marginTop: 30} : {}}>
      {props.label 
        ?<InputLabel htmlFor={id} shrink={true} className={classes.inputLabel} error={!!error}>{props.label}</InputLabel>
        : ''
      }
      <MUIRichTextEditor
        toolbar={controls.length > 0}
        defaultValue={content}
        draftEditorProps={draftEditorProps}
        id={id}
        aria-describedby={`${id}-helper-text ${id}-limit ${id}-error`}
        label={props.placeholder}
        inlineToolbar={false}
        // inlineToolbarControls={props.inlineToolbarControls} // Consider adding back in when styles have been corrected
        inlineToolbarControls={false}
        keyCommand={null}
        onChange={onRTEChange}
        controls={controls}
        keyCommands={generateKeyCommands(controls)}
      />

      <ErrorHelpText error={error} id={id} />

      { props.max ?
        <FormHelperText id={id + '-limit'} className={classes.charCount} variant="filled" error={charCount > props.max}>{charCount} of {props.max} Character Limit</FormHelperText>
      : '' }

      { props.help ?
        <FormHelperText id={id + '-helper-text'} variant="filled">{props.help}</FormHelperText>
      : '' }

    </FormElementWrapper>
  );
})

RichTextEditor.propTypes = {
  defaultValue: PropTypes.string,
  name: PropTypes.string,
  disallowIndent: PropTypes.bool,
  max: PropTypes.number,
}
